import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./searchable-select";
import { FormDialog, Field } from "./form-dialog";
import { nameField, isoDate, lookupValue } from "@/lib/validators";

const schema = z.object({
  title: nameField(120),
  client: lookupValue("Pick a client"),
  shootDate: isoDate,
  imageCount: z.coerce.number().int("Whole numbers only").min(0).max(10_000),
  status: z.enum(["uploading", "proofing", "selected", "delivered"]),
});
type Values = z.infer<typeof schema>;

export function NewGalleryDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", client: "", shootDate: "", imageCount: 0, status: "uploading" },
  });
  const [clientLabel, setClientLabel] = useState("");
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> New gallery</Button>}
      title="Create a gallery"
      description="Start a proofing gallery. You can upload images after creation."
      submitLabel="Create gallery"
      isDirty={isDirty}
      onReset={() => { reset(); setClientLabel(""); }}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Gallery created", { description: v.title });
          resolve();
        }, () => reject())();
      })}
    >
      <Field label="Title" htmlFor="g-title" error={errors.title?.message}>
        <Input id="g-title" placeholder="Vogue Oct Issue" {...register("title")} />
      </Field>
      <Field label="Client" error={errors.client?.message}>
        <SearchableSelect
          resource="clients"
          value={watch("client")}
          selectedLabel={clientLabel}
          onChange={(v, label) => {
            setValue("client", v, { shouldValidate: true, shouldDirty: true });
            setClientLabel(label);
          }}
          placeholder="Select client"
          searchPlaceholder="Search clients…"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Shoot date" htmlFor="g-date" error={errors.shootDate?.message} className="col-span-2">
          <Input id="g-date" type="date" {...register("shootDate")} />
        </Field>
        <Field label="# Images" htmlFor="g-count" error={errors.imageCount?.message}>
          <Input id="g-count" type="number" min={0} {...register("imageCount")} />
        </Field>
      </div>
      <Field label="Status" error={errors.status?.message}>
        <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="uploading">Uploading</SelectItem>
            <SelectItem value="proofing">Proofing</SelectItem>
            <SelectItem value="selected">Selected</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </FormDialog>
  );
}
